import { jsx } from "react/jsx-runtime";
import { cx } from "@/styled-system/css";
import { Box, FormField, Input, FormHelp, Label } from "@zillow/constellation-10";
import PropertySearch from "../../node_modules/@zillow/property-search/es/PropertySearch.esm.js";
import { useRef, useState, useLayoutEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { applyPropertyReportAddressInputRim, propertyReportTextEntryDefaultInsetShadowCss, getPropertyReportFilledFieldSurfaceTextOnly } from "./utils/common.esm.js";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
const ADDRESS_FORM_NAME = "address";
function stripConstellationInputBorderShadowClasses(className) {
  if (!(className == null ? void 0 : className.trim())) {
    return className;
  }
  return className.split(/\s+/).filter((token) => {
    if (!token) {
      return false;
    }
    if (!token.includes("bx-sh")) {
      return true;
    }
    return !/Input[._\\]border/i.test(token);
  }).join(" ");
}
const PROPERTY_SEARCH_CONFIG_VALUES = {
  prod: {
    addressAutocompleteURL: "https://www.zillowstatic.com/autocomplete/v3/suggestions/"
  },
  test: {
    addressAutocompleteURL: "https://static.qa.zillow.net/autocomplete/v3/suggestions/"
  }
};
const AddressAutocompleteField = ({
  onSelect,
  placeholder = "Enter the complete property address",
  label = "Property Address",
  description,
  required = false,
  error,
  onBlur: addressOnBlur,
  inputCssExtra,
  formHelpCss
}) => {
  var _a;
  const {
    environment = "prod"
  } = useBlockEnvironment();
  const {
    control
  } = useFormContext();
  const addressForDisplay = String(useWatch({
    control,
    name: ADDRESS_FORM_NAME,
    defaultValue: ""
  }) ?? "");
  const fieldRootRef = useRef(null);
  const inputRef = useRef(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hasError = Boolean(error);
  const handleChange = (value) => {
    onSelect(value);
  };
  const handleSubmit = (selection) => {
    const selectedAddress = (selection == null ? void 0 : selection.address) || addressForDisplay;
    const zpid = selection == null ? void 0 : selection.zpid;
    if (selectedAddress) {
      onSelect(selectedAddress, zpid);
    }
  };
  useLayoutEffect(() => {
    const root = fieldRootRef.current;
    if (!root) {
      return;
    }
    const onFocusIn = (e) => {
      const t = e.target;
      if (t instanceof Node && root.contains(t)) {
        setIsFocusWithin(true);
      }
    };
    const onFocusOut = (e) => {
      const fe = e;
      const next = fe.relatedTarget;
      if (next == null) {
        setIsFocusWithin(false);
        return;
      }
      if (next instanceof Node && root.contains(next)) {
        return;
      }
      setIsFocusWithin(false);
    };
    root.addEventListener("focusin", onFocusIn, true);
    root.addEventListener("focusout", onFocusOut, true);
    return () => {
      root.removeEventListener("focusin", onFocusIn, true);
      root.removeEventListener("focusout", onFocusOut, true);
    };
  }, []);
  useLayoutEffect(() => {
    applyPropertyReportAddressInputRim(inputRef.current, {
      error: hasError,
      isFocusWithin,
      isHovered
    });
  }, [hasError, isFocusWithin, isHovered]);
  return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(FormField, { error: hasError, label: /* @__PURE__ */ jsx(Label, { css: {
    paddingBottom: "spacing.50",
    fontWeight: "fontWeights.bold",
    color: "text.neutral"
  }, children: label }), required, description: /* @__PURE__ */ jsx(FormHelp, { error: hasError, ...formHelpCss ? {
    css: formHelpCss
  } : {}, children: error || description }), control: /* @__PURE__ */ jsx(Box, { ref: fieldRootRef, css: {
    width: "100%"
  }, children: /* @__PURE__ */ jsx(PropertySearch, { renderEmptyState: null, renderInput: (props) => {
    const {
      className: hostClassName,
      onChange: comboboxOnChange,
      onBlur: comboboxOnBlur,
      ...restProps
    } = props;
    const safeRestProps = {
      ...restProps
    };
    delete safeRestProps.className;
    delete safeRestProps.style;
    delete safeRestProps.css;
    for (const key of ["appearance", "editable", "tags", "limitTagsShown", "onClear", "onTagClose"]) {
      delete safeRestProps[key];
    }
    return /* @__PURE__ */ jsx(Input, { ...safeRestProps, ref: inputRef, className: cx(stripConstellationInputBorderShadowClasses(hostClassName)), required, error: hasError, placeholder, value: addressForDisplay, onChange: (e) => {
      comboboxOnChange == null ? void 0 : comboboxOnChange(e);
      handleChange(e.target.value);
    }, onMouseEnter: () => {
      setIsHovered(true);
    }, onMouseLeave: () => {
      setIsHovered(false);
    }, onBlur: (e) => {
      var _a2;
      comboboxOnBlur == null ? void 0 : comboboxOnBlur(e);
      (_a2 = addressOnBlur || props.onBlur) == null ? void 0 : _a2(e);
    }, css: {
      ...getPropertyReportFilledFieldSurfaceTextOnly(hasError),
      ...inputCssExtra,
      ...!hasError ? propertyReportTextEntryDefaultInsetShadowCss : {}
    } });
  }, addressAutocompleteUrl: ((_a = PROPERTY_SEARCH_CONFIG_VALUES == null ? void 0 : PROPERTY_SEARCH_CONFIG_VALUES[environment]) == null ? void 0 : _a.addressAutocompleteURL) || PROPERTY_SEARCH_CONFIG_VALUES.prod.addressAutocompleteURL, onChange: handleChange, onSubmit: handleSubmit }) }) }) });
};
export {
  AddressAutocompleteField as default
};
